import { c as _c } from "react/compiler-runtime";
import { Box, Stack, Typography, useTheme } from '@mui/material';
import Decimal from 'decimal.js';
import React, { useEffect, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { Button } from '@/components/form/baseInputs/Button';
import { FormAwareButtonGroup } from '@/components/form/formAwareInputs/FormAwareButtonGroup';
import { FormAwareCheckbox } from '@/components/form/formAwareInputs/FormAwareCheckbox';
import { FormAwareDatePickerInput } from '@/components/form/formAwareInputs/FormAwareDatePickerInput';
import { FormAwareHiddenInput } from '@/components/form/formAwareInputs/FormAwareHiddenInput';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { Card } from '@/components/layout/Card/Card';
import { Callout } from '@/components/notifications/Callout/Callout';
import { useFormContext } from '@/components/react-hook-form';
import { useCurrentAssetValuation, useInitialAssetValuation } from '@/modules/assetValuation/useAssetValuation';
import { DocumentUploaderWithList } from '@/modules/documents/DocumentUploaderWithList/DocumentUploaderWithList';
import { AssetValuationV2ValuationReason, DocumentType } from '@/types/schema';
import { getNextDay } from '@/utils/dateUtils';
import { formatCurrency } from '@/utils/formatting/currency';
import { AssetDetailsModal, AssetDetailsModalProps } from '../AssetDetailsModal/AssetDetailsModal';
import { useAssetsFieldArray } from '../AssetDetailsModal/useAssetsFieldArray';
import { AssetFormTable } from '../AssetFormTable/AssetFormTable';
import { Asset, NEW_ASSET_ID } from '../types/asset';
import { getCalculatedFundingValueFromAssets, getSelectedAsset, mapQueryAssetsToAssets } from '../utils';
import { FundingDateInput } from './FundingDateInput';
import { Fields, NAMESPACE, SubformComponentProps, SubformField } from './types';
export function AssetsSubformComponent(t0) {
  const $ = _c(112);
  const {
    householdId,
    entityId,
    variant,
    disabled,
    forceConfirmValuation,
    dateCopy,
    assetSumLabelCopy
  } = t0;
  const {
    control,
    resetField
  } = useFormContext();
  const isVerifyInitialFunding = variant === "verifyInitialFunding";
  const isInitialFundingRevaluation = variant === "initialFundingRevaluation";
  const isEditDesignSummary = variant === "editDesignSummary";
  const theme = useTheme();
  let t1;
  if ($[0] !== control) {
    t1 = {
      name: `${NAMESPACE}.assets` as const satisfies SubformField,
      control
    };
    $[0] = control;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const assets = useWatch(t1);
  const {
    removeByAsset,
    updateByAsset,
    handleAssetsModalSubmit
  } = useAssetsFieldArray(`${NAMESPACE}.assets` as const satisfies SubformField, control, assets);
  const [newAsset, setNewAsset] = React.useState(null);
  const [createDocumentErrorMessage, setCreateDocumentErrorMessage] = React.useState(null);
  let t2;
  if ($[2] !== handleAssetsModalSubmit) {
    t2 = (value, opts) => {
      handleAssetsModalSubmit(value, opts);
      setNewAsset(null);
    };
    $[2] = handleAssetsModalSubmit;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const onAssetModalSubmit = t2;
  const currentAssetValuation = useCurrentAssetValuation(entityId);
  const initialAssetValuation = useInitialAssetValuation(entityId);
  let t3;
  if ($[4] !== currentAssetValuation?.accountId || $[5] !== currentAssetValuation?.assetValues || $[6] !== currentAssetValuation?.id || $[7] !== forceConfirmValuation || $[8] !== initialAssetValuation?.accountId || $[9] !== initialAssetValuation?.assetValues || $[10] !== initialAssetValuation?.id || $[11] !== variant) {
    bb0: {
      const mapQueryAssetsOpts = {
        markAsConfirmed: !forceConfirmValuation
      };
      if (variant === "initialFundingRevaluation" || variant === "verifyInitialFunding") {
        const formattedAssets = mapQueryAssetsToAssets(initialAssetValuation?.assetValues ?? [], mapQueryAssetsOpts);
        t3 = {
          formattedAssets,
          valuationId: initialAssetValuation?.id,
          accountId: initialAssetValuation?.accountId
        };
        break bb0;
      }
      const formattedAssets_0 = mapQueryAssetsToAssets(currentAssetValuation?.assetValues ?? [], mapQueryAssetsOpts);
      const t4 = currentAssetValuation?.id;
      const t5 = currentAssetValuation?.accountId;
      let t6;
      if ($[13] !== formattedAssets_0 || $[14] !== t4 || $[15] !== t5) {
        t6 = {
          formattedAssets: formattedAssets_0,
          valuationId: t4,
          accountId: t5
        };
        $[13] = formattedAssets_0;
        $[14] = t4;
        $[15] = t5;
        $[16] = t6;
      } else {
        t6 = $[16];
      }
      t3 = t6;
    }
    $[4] = currentAssetValuation?.accountId;
    $[5] = currentAssetValuation?.assetValues;
    $[6] = currentAssetValuation?.id;
    $[7] = forceConfirmValuation;
    $[8] = initialAssetValuation?.accountId;
    $[9] = initialAssetValuation?.assetValues;
    $[10] = initialAssetValuation?.id;
    $[11] = variant;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const {
    formattedAssets: formattedAssets_1,
    valuationId,
    accountId
  } = t3;
  let t4;
  let t5;
  if ($[17] !== isInitialFundingRevaluation || $[18] !== isVerifyInitialFunding || $[19] !== resetField) {
    t4 = () => {
      if (!isInitialFundingRevaluation && !isVerifyInitialFunding) {
        resetField(`${NAMESPACE}.valuationDate` as const satisfies SubformField, {
          defaultValue: null
        });
      } else {
        if (isVerifyInitialFunding) {
          resetField(`${NAMESPACE}.fundingDate` as const satisfies SubformField, {
            defaultValue: null
          });
        }
      }
    };
    t5 = [resetField, isVerifyInitialFunding, isInitialFundingRevaluation];
    $[17] = isInitialFundingRevaluation;
    $[18] = isVerifyInitialFunding;
    $[19] = resetField;
    $[20] = t4;
    $[21] = t5;
  } else {
    t4 = $[20];
    t5 = $[21];
  }
  useEffect(t4, t5);
  let t6;
  if ($[22] !== accountId || $[23] !== assets.length || $[24] !== formattedAssets_1 || $[25] !== initialAssetValuation || $[26] !== resetField || $[27] !== valuationId) {
    t6 = () => {
      if (assets.length || !initialAssetValuation) {
        return;
      }
      resetField(`${NAMESPACE}.assets` as const, {
        defaultValue: formattedAssets_1
      });
      resetField(`${NAMESPACE}.valuationId` as const, {
        defaultValue: valuationId
      });
      resetField(`${NAMESPACE}.accountId` as const, {
        defaultValue: accountId
      });
    };
    $[22] = accountId;
    $[23] = assets.length;
    $[24] = formattedAssets_1;
    $[25] = initialAssetValuation;
    $[26] = resetField;
    $[27] = valuationId;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  let t7;
  if ($[29] !== accountId || $[30] !== assets || $[31] !== formattedAssets_1 || $[32] !== initialAssetValuation || $[33] !== resetField || $[34] !== valuationId) {
    t7 = [formattedAssets_1, valuationId, resetField, assets, accountId, initialAssetValuation];
    $[29] = accountId;
    $[30] = assets;
    $[31] = formattedAssets_1;
    $[32] = initialAssetValuation;
    $[33] = resetField;
    $[34] = valuationId;
    $[35] = t7;
  } else {
    t7 = $[35];
  }
  useEffect(t6, t7);
  let t8;
  if ($[36] !== initialAssetValuation?.effectiveDate) {
    t8 = initialAssetValuation?.effectiveDate ?? new Date(new Date().toISOString());
    $[36] = initialAssetValuation?.effectiveDate;
    $[37] = t8;
  } else {
    t8 = $[37];
  }
  const entityEffectiveDate = t8;
  let t9;
  if ($[38] !== newAsset || $[39] !== onAssetModalSubmit) {
    t9 = newAsset && <AssetDetailsModal onSubmit={onAssetModalSubmit} asset={newAsset} isOpen={true} isNewAsset={true} onClose={() => setNewAsset(null)} />;
    $[38] = newAsset;
    $[39] = onAssetModalSubmit;
    $[40] = t9;
  } else {
    t9 = $[40];
  }
  let t10;
  if ($[41] !== isInitialFundingRevaluation) {
    t10 = isInitialFundingRevaluation && <Callout severity="warning">Annuity payment schedules will automatically be recalculated based on the new initial funding amounts. The taxable gift from funding the GRAT has not been changed. In order to edit the taxable gift amount, please edit the GRAT details.</Callout>;
    $[41] = isInitialFundingRevaluation;
    $[42] = t10;
  } else {
    t10 = $[42];
  }
  let t11;
  if ($[43] !== t10) {
    t11 = <Box display="flex">{t10}</Box>;
    $[43] = t10;
    $[44] = t11;
  } else {
    t11 = $[44];
  }
  const t12 = !initialAssetValuation && !currentAssetValuation;
  const t13 = variant === "verifyInitialFunding" ? "Review" : undefined;
  const t14 = variant === "verifyInitialFunding" || variant === "initialFundingRevaluation";
  let t15;
  if ($[45] !== assets || $[46] !== disabled || $[47] !== removeByAsset || $[48] !== t12 || $[49] !== t13 || $[50] !== t14 || $[51] !== updateByAsset) {
    t15 = <AssetFormTable assetList={assets} removeByAsset={removeByAsset} updateByAsset={updateByAsset} loading={t12} disabled={disabled} updateAssetButtonText={t13} isInitialValuation={t14} />;
    $[45] = assets;
    $[46] = disabled;
    $[47] = removeByAsset;
    $[48] = t12;
    $[49] = t13;
    $[50] = t14;
    $[51] = updateByAsset;
    $[52] = t15;
  } else {
    t15 = $[52];
  }
  let t16;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      marginTop: 2
    };
    $[53] = t16;
  } else {
    t16 = $[53];
  }
  let t17;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = () => {
      setNewAsset(getSelectedAsset([], NEW_ASSET_ID));
    };
    $[54] = t17;
  } else {
    t17 = $[54];
  }
  let t18;
  if ($[55] !== disabled) {
    t18 = <Button fullWidth={true} whiteSpace="nowrap" sx={t16} startIcon={PlusIcon} size="sm" variant="secondary" disabled={disabled} onClick={t17}>Add additional asset</Button>;
    $[55] = disabled;
    $[56] = t18;
  } else {
    t18 = $[56];
  }
  let t19;
  if ($[57] !== t15 || $[58] !== t18) {
    t19 = <Box>{t15}{t18}</Box>;
    $[57] = t15;
    $[58] = t18;
    $[59] = t19;
  } else {
    t19 = $[59];
  }
  let t20;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = {
      p: 2
    };
    $[60] = t20;
  } else {
    t20 = $[60];
  }
  let t21;
  if ($[61] !== control || $[62] !== dateCopy || $[63] !== disabled || $[64] !== entityEffectiveDate || $[65] !== isEditDesignSummary || $[66] !== isInitialFundingRevaluation || $[67] !== isVerifyInitialFunding) {
    t21 = !isInitialFundingRevaluation && !isVerifyInitialFunding && !isEditDesignSummary && <Stack direction="row" spacing={2}><Box width={320}><FormAwareButtonGroup control={control} fieldName={`${NAMESPACE}.valuationReason` as const satisfies SubformField} variant="light" required={true} label="Valuation type" options={[{
          display: "Revaluation",
          value: AssetValuationV2ValuationReason.AssetRevaluation
        }, {
          display: "Asset substitution",
          value: AssetValuationV2ValuationReason.GratAssetSubstitution
        }]} /></Box><FundingDateInput control={control} label={dateCopy || "Valuation date"} disabled={disabled} fieldName={`${NAMESPACE}.valuationDate` as const satisfies SubformField} minDate={getNextDay(entityEffectiveDate)} /></Stack>;
    $[61] = control;
    $[62] = dateCopy;
    $[63] = disabled;
    $[64] = entityEffectiveDate;
    $[65] = isEditDesignSummary;
    $[66] = isInitialFundingRevaluation;
    $[67] = isVerifyInitialFunding;
    $[68] = t21;
  } else {
    t21 = $[68];
  }
  let t22;
  if ($[69] !== control || $[70] !== dateCopy || $[71] !== disabled || $[72] !== isVerifyInitialFunding) {
    t22 = isVerifyInitialFunding && <Stack><FormAwareDatePickerInput disabled={disabled} control={control} required={true} label={dateCopy || "Initial funding date"} fieldName={`${NAMESPACE}.fundingDate` as const satisfies SubformField} /></Stack>;
    $[69] = control;
    $[70] = dateCopy;
    $[71] = disabled;
    $[72] = isVerifyInitialFunding;
    $[73] = t22;
  } else {
    t22 = $[73];
  }
  let t23;
  if ($[74] !== control || $[75] !== disabled || $[76] !== isInitialFundingRevaluation || $[77] !== theme) {
    t23 = isInitialFundingRevaluation && <Stack><Stack direction="row" spacing={1}><FormAwareCheckbox control={control} disabled={disabled} required={true} fieldName={`${NAMESPACE}.confirm` as const satisfies SubformField} /><Typography variant="body-xs" maxWidth={theme.spacing(24)}>I confirm that I wish to revise the initial funding value</Typography></Stack></Stack>;
    $[74] = control;
    $[75] = disabled;
    $[76] = isInitialFundingRevaluation;
    $[77] = theme;
    $[78] = t23;
  } else {
    t23 = $[78];
  }
  let t24;
  if ($[79] !== control) {
    t24 = <FormAwareHiddenInput control={control} fieldName={`${NAMESPACE}.valuationId` as const satisfies SubformField} />;
    $[79] = control;
    $[80] = t24;
  } else {
    t24 = $[80];
  }
  let t25;
  if ($[81] !== control) {
    t25 = <FormAwareHiddenInput control={control} fieldName={`${NAMESPACE}.accountId` as const satisfies SubformField} />;
    $[81] = control;
    $[82] = t25;
  } else {
    t25 = $[82];
  }
  let t26;
  if ($[83] !== assetSumLabelCopy) {
    t26 = <Typography variant="body-xs">{assetSumLabelCopy}</Typography>;
    $[83] = assetSumLabelCopy;
    $[84] = t26;
  } else {
    t26 = $[84];
  }
  let t27;
  if ($[85] !== assets) {
    t27 = formatCurrency(getCalculatedFundingValueFromAssets(assets) ?? new Decimal(0));
    $[85] = assets;
    $[86] = t27;
  } else {
    t27 = $[86];
  }
  let t28;
  if ($[87] !== t27) {
    t28 = <Typography variant="title-xl" component="p">{t27}</Typography>;
    $[87] = t27;
    $[88] = t28;
  } else {
    t28 = $[88];
  }
  let t29;
  if ($[89] !== t26 || $[90] !== t28) {
    t29 = <Stack>{t26}{t28}</Stack>;
    $[89] = t26;
    $[90] = t28;
    $[91] = t29;
  } else {
    t29 = $[91];
  }
  let t30;
  if ($[92] !== t21 || $[93] !== t22 || $[94] !== t23 || $[95] !== t24 || $[96] !== t25 || $[97] !== t29) {
    t30 = <Card variant="filled" sx={t20}><Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>{t21}{t22}{t23}{t24}{t25}{t29}</Stack></Card>;
    $[92] = t21;
    $[93] = t22;
    $[94] = t23;
    $[95] = t24;
    $[96] = t25;
    $[97] = t29;
    $[98] = t30;
  } else {
    t30 = $[98];
  }
  let t31;
  if ($[99] !== createDocumentErrorMessage || $[100] !== entityId || $[101] !== householdId || $[102] !== isEditDesignSummary) {
    t31 = !isEditDesignSummary && <Stack pt={2} alignItems="center"><Box width="80%"><DocumentUploaderWithList uploaderLabel="Upload supporting documentation (optional)" listDocumentsLike={{
          hasEntityWith: [{
            id: entityId
          }],
          type: DocumentType.AssetValuation
        }} errorMessage={createDocumentErrorMessage || undefined} getCreateDocumentInput={file => ({
          householdID: householdId,
          name: file.fileName,
          entityID: entityId,
          type: DocumentType.AssetValuation,
          fileID: file.fileId
        })} onDocumentUploadSuccess={() => setCreateDocumentErrorMessage(null)} onDocumentUploadError={() => setCreateDocumentErrorMessage("We weren't able to complete your document upload process. Please try again.")} householdId={householdId} /></Box></Stack>;
    $[99] = createDocumentErrorMessage;
    $[100] = entityId;
    $[101] = householdId;
    $[102] = isEditDesignSummary;
    $[103] = t31;
  } else {
    t31 = $[103];
  }
  let t32;
  if ($[104] !== t11 || $[105] !== t19 || $[106] !== t30 || $[107] !== t31) {
    t32 = <Stack spacing={2}>{t11}{t19}{t30}{t31}</Stack>;
    $[104] = t11;
    $[105] = t19;
    $[106] = t30;
    $[107] = t31;
    $[108] = t32;
  } else {
    t32 = $[108];
  }
  let t33;
  if ($[109] !== t32 || $[110] !== t9) {
    t33 = <>{t9}{t32}</>;
    $[109] = t32;
    $[110] = t9;
    $[111] = t33;
  } else {
    t33 = $[111];
  }
  return t33;
}